/**
 * ErrorBoundary — перехват падений React-рендера (P0-5, 1.31.0)
 *
 * Зачем:
 *  • Ошибка в рендере компонента роняет всё дерево React в белый экран.
 *    window.onerror её не всегда ловит (React перехватывает). Нужен
 *    именно class-компонент с componentDidCatch.
 *  • Логируем падение в Supabase (logError) — чтобы мы увидели его до
 *    звонка от пилотной компании.
 *  • Показываем пользователю аккуратный фирменный экран с кнопкой
 *    «Перезагрузить», а не голый белый фон.
 *
 * Размещение:
 * Оборачивает всё дерево внутри App (см. App.tsx). Один на приложение
 * достаточно: для пилота нам не нужны гранулярные boundary вокруг
 * отдельных виджетов — важно поймать любое падение и не потерять его.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { C, F } from '../design-system/tokens';
import { logError } from './errorLog';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    // Переключаемся на fallback-экран при любой ошибке рендера.
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Best-effort лог в Supabase. logError сам ничего не бросает.
    void logError({
      source: 'react',
      message: error.message || 'React render error',
      stack: error.stack ?? null,
      context: {
        componentStack: info.componentStack?.slice(0, 4000) ?? null,
      },
    });
  }

  private handleReload = () => {
    // Полная перезагрузка: проще и надёжнее, чем пытаться восстановить
    // повреждённое дерево состояния. Для пилота — приемлемо.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          height: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 18,
          padding: 32,
          background: C.bg,
          color: C.txt,
          fontFamily: F.body,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: C.accS,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 30,
          }}
          aria-hidden
        >
          ⚠️
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, fontFamily: F.head }}>
          Что-то пошло не так
        </div>
        <div style={{ fontSize: 14, color: C.sub, maxWidth: 320, lineHeight: 1.5 }}>
          Приложение столкнулось с ошибкой. Мы уже получили уведомление.
          Попробуйте перезагрузить — данные, сохранённые офлайн, не потеряются.
        </div>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            marginTop: 8,
            padding: '14px 28px',
            borderRadius: 14,
            border: 'none',
            background: C.acc,
            color: '#fff',
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: C.accG,
          }}
        >
          Перезагрузить
        </button>
      </div>
    );
  }
}
